/**
 * @fileoverview Admin MFA Management Hook
 * 
 * Provides administrative functions for managing Multi-Factor Authentication (MFA)
 * for user accounts. Allows admins to disable MFA for users who have lost access
 * to their authenticator devices or need emergency account access.
 * 
 * Security Considerations:
 * - Only users with admin role can disable MFA for others
 * - All MFA disables are logged with reason and admin ID for audit trail
 * - Notifications are sent to affected users
 * - Users can re-enable MFA immediately after it's disabled
 * 
 * Key Features:
 * - Disable MFA with audit logging
 * - Reason tracking for compliance
 * - Toast notifications for feedback
 * - Loading state management
 * 
 * @module useAdminMFA
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

/**
 * Hook for admin-level MFA management
 * 
 * Provides functionality for administrators to disable MFA on behalf of users
 * who have lost access to their authenticator devices or need emergency support.
 * 
 * The disable operation:
 * 1. Verifies the requesting user is an admin
 * 2. Removes the user's MFA factors from Supabase Auth
 * 3. Logs the action with reason and admin ID
 * 4. Sends notification to the affected user
 * 5. Returns success/error status
 * 
 * @returns Object with disableUserMFA function and loading state
 * 
 * @example
 * ```tsx
 * function AdminMFAControls({ userId }) {
 *   const { disableUserMFA, isDisabling } = useAdminMFA();
 * 
 *   const handleDisable = async () => {
 *     try {
 *       await disableUserMFA(
 *         userId,
 *         'User lost authenticator device and requested reset'
 *       );
 *       alert('MFA disabled successfully. User can now login and set up MFA again.');
 *     } catch (error) {
 *       console.error('Failed to disable MFA:', error);
 *     }
 *   };
 * 
 *   return (
 *     <button onClick={handleDisable} disabled={isDisabling}>
 *       {isDisabling ? 'Disabling MFA...' : 'Disable User MFA'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useAdminMFA() {
  const [isDisabling, setIsDisabling] = useState(false);

  /**
   * Disables MFA for a specific user
   * 
   * This is an administrative action that removes all MFA factors for a user,
   * allowing them to log in without two-factor authentication. The action is
   * logged for security audit purposes.
   * 
   * Common use cases:
   * - User lost authenticator device
   * - User locked out due to MFA issues
   * - Emergency account access needed
   * - User requested MFA reset
   * 
   * @param userId - The UUID of the user to disable MFA for
   * @param reason - Explanation for disabling MFA (logged for audit)
   * @throws Error if user is not admin or operation fails
   * @returns Result object with success status and message
   */
  const disableUserMFA = async (userId: string, reason: string) => {
    setIsDisabling(true);
    try {
      const { data, error } = await supabase.rpc('admin_disable_user_mfa', {
        p_target_user_id: userId,
        p_reason: reason,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string; details?: any };

      if (!result.success) {
        throw new Error(result.error || 'Failed to disable MFA');
      }

      toast({
        title: 'MFA Disabled',
        description: `Two-factor authentication has been disabled for this user.`,
      });

      return result;
    } catch (error) {
      console.error('Error disabling user MFA:', error);
      toast({
        title: 'Failed to disable MFA',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsDisabling(false);
    }
  };

  return {
    disableUserMFA,
    isDisabling,
  };
}
